import type { ReactNode } from "react";
import { useParams } from "react-router";
import { useGuardedNavigate } from "@/lib/utils/unsaved-changes";
import { useYard, YardScreenHeader } from "./components";

export type YardSettingsSectionProps = {
  /** Label of the configuration step, shown under the yard name. */
  title: string;
  children: ReactNode;
};

/**
 * Layout for a single yard configuration step reached from the
 * "Menu paramétrage chantier" hub (screen 903). Handles the back link to the
 * hub, the shared yard header and the loading / not-found states.
 */
export function YardSettingsSection({
  title,
  children,
}: YardSettingsSectionProps) {
  const { yardId } = useParams<{ yardId: string }>();
  const navigate = useGuardedNavigate();
  const numericYardId = Number(yardId);
  const { yard, loading } = useYard(numericYardId);

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex min-h-[200px] items-center justify-center text-gray-500">
          Chargement...
        </div>
      </div>
    );
  }

  if (!yard) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex min-h-[200px] items-center justify-center text-gray-500">
          Chantier introuvable
        </div>
      </div>
    );
  }

  return (
    // Bounded to the height AdminLayout hands down (screen-design "no
    // page-level scroll" rule): the header stays fixed and the step below
    // gets the remaining height, so its table can scroll on its own.
    <div className="container mx-auto flex h-full min-h-0 flex-col overflow-hidden p-6">
      <YardScreenHeader
        yardName={yard.name}
        yardState={yard.state}
        screenTitle={title}
        onBack={() => navigate(`/admin/yards/${numericYardId}`)}
        backLabel="Retour au menu de paramétrage"
      />
      {/* Steps not yet on the bounded-table pattern still scroll here. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-none">
        {children}
      </div>
    </div>
  );
}
